import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { PaperPlaneRight, Broom, Armchair, UserCircleGear, Lightning } from "@phosphor-icons/react";

const ROLE_COLORS = { admin: "#2F4F4F", host: "#F4A261", server: "#81B29A" };

const PRESETS = [
  { text: "Table needs bussing", icon: Broom },
  { text: "Ready for next seating", icon: Armchair },
  { text: "Need manager", icon: UserCircleGear },
];

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const suffix = h >= 12 ? "PM" : "AM";
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display}:${m} ${suffix}`;
};

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages?limit=100");
      setMessages(data);
    } catch { /* silent */ }
  }, []);

  const markRead = useCallback(async () => {
    try {
      await api.put("/chat/messages/read");
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchMessages();
    markRead();
  }, [fetchMessages, markRead]);

  // WebSocket for real-time messages
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "chat_message" && msg.message) {
          setMessages((prev) => [...prev, msg.message]);
          markRead();
        }
      };
      ws.onclose = () => setTimeout(() => {}, 3000);
    } catch { /* silent */ }
    return () => ws?.close();
  }, [markRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (msgText, isPreset = false) => {
    if (!msgText.trim()) return;
    setSending(true);
    try {
      await api.post("/chat/messages", { text: msgText.trim(), is_preset: isPreset });
      setText("");
      inputRef.current?.focus();
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(text);
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-120px)]" data-testid="chat-page">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 border-b border-stone-200 bg-white flex-shrink-0">
        <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Service Chat</h2>
        <p className="text-[10px] text-stone-500">{messages.length} messages today</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-16">
            <Lightning size={40} className="mx-auto mb-2 text-stone-200" weight="duotone" />
            <p className="text-sm text-stone-500">No messages yet. Send a quick update!</p>
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${msg.id}`}>
              <div className={`max-w-[75%] ${isMe ? "items-end" : "items-start"}`}>
                {!isMe && (
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold" style={{ backgroundColor: ROLE_COLORS[msg.sender_role] || "#9CA3AF" }}>
                      {msg.sender_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="text-[10px] font-medium text-stone-600">{msg.sender_name}</span>
                    <Badge className="text-[8px] px-1 py-0" style={{ backgroundColor: (ROLE_COLORS[msg.sender_role] || "#9CA3AF") + "20", color: ROLE_COLORS[msg.sender_role] || "#9CA3AF", border: "none" }}>
                      {msg.sender_role}
                    </Badge>
                  </div>
                )}
                <div
                  className={`px-3 py-2 rounded-2xl text-sm ${
                    isMe
                      ? "bg-[#2F4F4F] text-white rounded-br-md"
                      : "bg-white border border-stone-200 text-stone-800 rounded-bl-md"
                  } ${msg.is_preset ? "font-medium" : ""}`}
                >
                  {msg.is_preset && <Lightning size={12} weight="fill" className={`inline mr-1 ${isMe ? "text-white/60" : "text-amber-500"}`} />}
                  {msg.text}
                </div>
                <span className={`text-[9px] text-stone-500 mt-0.5 block ${isMe ? "text-right" : ""}`}>
                  {formatTime(msg.created_at)}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Quick Presets */}
      <div className="px-4 sm:px-6 py-2 border-t border-stone-100 bg-stone-50 flex gap-2 flex-shrink-0 overflow-x-auto" data-testid="chat-presets">
        {PRESETS.map((p) => (
          <button
            key={p.text}
            onClick={() => sendMessage(p.text, true)}
            disabled={sending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-200 bg-white text-xs font-medium text-stone-600 hover:bg-stone-100 hover:border-stone-300 transition-all whitespace-nowrap flex-shrink-0"
            data-testid={`preset-${p.text.toLowerCase().replace(/\s/g, "-")}`}
          >
            <p.icon size={14} weight="duotone" />
            {p.text}
          </button>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-3 border-t border-stone-200 bg-white flex gap-2 flex-shrink-0" data-testid="chat-input-form">
        <Input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message..."
          className="flex-1"
          disabled={sending}
          data-testid="chat-text-input"
        />
        <Button type="submit" disabled={sending || !text.trim()} style={{ backgroundColor: "#2F4F4F" }} data-testid="chat-send-btn">
          <PaperPlaneRight size={18} weight="fill" />
        </Button>
      </form>
    </div>
  );
}
